
import { parseAddressResponse, toCreateOrUpdateAddressRequest } from 'features/declaration/address/models/mapper'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { LocationOfGoodsType } from 'features/declaration/models/schemas/locationOfGoodsSchema'
import {
  ActiveBorderTransportMeansType,
  CountriesOfRoutingType,
  DepartureTransportMeansType,
} from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { excludeDeleted, sortBySequenceNumber } from 'shared/lib/common-util'
import { formatToIsoDateTimeString, formatToLocalDateTimeString } from 'shared/lib/datetime'
import R0473Validator from 'features/declaration/form/validations/R0473'
import R0076Validator from 'features/declaration/form/validations/R0076'
import { nonNullAddress } from 'features/declaration/models/helpers'
import {
  parseCarrierConsignmentDetailResponse,
  parseConsigneeConsignmentDetailResponse, parseConsignorConsignmentDetailResponse,
} from 'features/declaration/consignment-detail/models/mapper'
import {
  parsePlaceOfAcceptance,
  parsePlaceOfDelivery,
  parsePlaceOfLoading,
  parsePlaceOfUnloading, toCreateOrUpdatePlaceRequest,
} from 'features/declaration/place/models/mapper'
import {
  parseTransportEquipmentResponse, toCreateOrUpdateTransportEquipmentRequest,
} from 'features/declaration/transport-equipment/models/mapper'
import {
  parseAdditionalSupplyChainActorResponse,
  toCreateOrUpdateAdditionalSupplyChainActorRequest,
} from '../../additional-supply-chain-actor/models/mapper'
import { Consignment, ConsignmentSummary } from './types'
import {
  ActiveBorderTransportMeansResponse,
  AddressRequest,
  ConsignmentResponse,
  CountryOfRoutingResponse,
  CreateOrUpdateActiveBorderTransportMeansRequest,
  CreateOrUpdateConsignmentRequest,
  CreateOrUpdateCountryOfRoutingRequest,
  CreateOrUpdateDepartureTransportMeansRequest,
  DepartureTransportMeansResponse,
  LocationOfGoodsResponse,
} from '../../../../api/models'
import { ConsignmentSave, GrossMassValidationResult } from './request'
import { ConsignmentSummaryResponse } from '../../../../api/models/consignment-summary-response'
import { GrossMassValidationResponse } from '../../../../api/models/gross-mass-validation-response'

export function toConsignmentSave(
  transitOperationId: number | null,
  formValues: TransitOperationFormValues,
  consignorId: number | null,
  consigneeId: number | null,
  carrierId: number | null,
): ConsignmentSave {
  return {
    transitOperationId,
    consigneeId,
    consignorId,
    carrierId,
    countryOfDispatch: formValues.countryOfDispatch,
    countryOfDestination: formValues.countryOfDestination,
    grossMass: formValues.grossMass,
    transportChargesMethodOfPayment: formValues.transportChargesMethodOfPayment,
    activeBorderTransportMeans: formValues.activeBorderTransportMeans,
    containerIndicator: formValues.containerIndicator,
    inlandModeOfTransport: formValues.inlandModeOfTransport,
    borderModeOfTransport: formValues.borderModeOfTransport,
    additionalInformation: formValues.additionalInformation,
    additionalReference: formValues.additionalReference,
    locationOfGoods: formValues.locationOfGoods,
    placeOfAcceptance: formValues.placeOfAcceptance,
    placeOfDelivery: formValues.placeOfDelivery,
    placeOfLoading: formValues.placeOfLoading,
    placeOfUnloading: formValues.placeOfUnloading,
    additionalSupplyChainActor: formValues.additionalSupplyChainActor,
    countryOfRoutingOfConsignment: formValues.countryOfRoutingOfConsignment,
    declarant: formValues.declarant,
    departureTransportMeans: formValues.departureTransportMeans,
    authorisations: formValues.authorisations,
    transportEquipment: formValues.transportEquipment,
    referenceNumberUCR: formValues.referenceNumberUCR,
    reducedDatasetIndicator: formValues.reducedDatasetIndicator,
  }
}

export function toCreateOrUpdateCountryOfRoutingRequest(
  item: CountriesOfRoutingType,
  index: number,
): CreateOrUpdateCountryOfRoutingRequest {
  return {
    id: item.id,
    sequenceNumber: index,
    country: item.country ?? '',
  }
}

export function parseCountryOfRoutingResponse(response: CountryOfRoutingResponse, index: number): CountriesOfRoutingType {
  return {
    id: response.id,
    deleted: false,
    country: response.country,
    sequenceNumber: response.sequenceNumber ?? index,
  }
}

export function toCreateOrUpdateActiveBorderTransportMeansRequest(
  borderMeansItem: ActiveBorderTransportMeansType,
  index: number,
): CreateOrUpdateActiveBorderTransportMeansRequest {
  const identificationNumber = R0076Validator.doesIdentificationNumberMustBeUpperCase(borderMeansItem)
    ? borderMeansItem.identificationNumber?.toUpperCase()
    : borderMeansItem.identificationNumber

  return {
    id: borderMeansItem.id,
    sequenceNumber: index,
    customsOfficeAtBorderReferenceNumber: borderMeansItem.customsOfficeAtBorderReferenceNumber,
    typeOfIdentification: borderMeansItem.typeOfIdentification,
    identificationNumber,
    nationality: borderMeansItem.nationality,
    conveyanceReferenceNumber: borderMeansItem.conveyanceReferenceNumber ?? '',
    typeOfMeansOfTransport: borderMeansItem.typeOfMeansOfTransport ?? '',
    actualDateAndTimeOfDeparture: formatToIsoDateTimeString(borderMeansItem.actualDateAndTimeOfDeparture),
    estimatedDateAndTimeOfDeparture: formatToIsoDateTimeString(borderMeansItem.estimatedDateAndTimeOfDeparture),
    estimatedDateAndTimeOfArrival: formatToIsoDateTimeString(borderMeansItem.estimatedDateAndTimeOfArrival),
    countryOfRoutings: borderMeansItem.countriesOfRouting.filter(excludeDeleted)
      .map(toCreateOrUpdateCountryOfRoutingRequest),
  }
}

export function toCreateOrUpdateDepartureTransportMeansRequest(
  departureMeansItem: DepartureTransportMeansType,
  index: number,
): CreateOrUpdateDepartureTransportMeansRequest {
  const identificationNumber = R0473Validator.doesIdentificationNumberMustBeUpperCase(departureMeansItem)
    ? departureMeansItem.identificationNumber?.toUpperCase()
    : departureMeansItem.identificationNumber

  return {
    id: departureMeansItem.id,
    sequenceNumber: index,
    typeOfIdentification: departureMeansItem.typeOfIdentification,
    nationality: departureMeansItem.nationality,
    identificationNumber,
  }
}

export function parseActiveBorderTransportMeansResponse(
  response: ActiveBorderTransportMeansResponse,
  index: number,
): ActiveBorderTransportMeansType {
  return {
    id: response.id,
    deleted: false,
    conveyanceReferenceNumber: response.conveyanceReferenceNumber ?? '',
    customsOfficeAtBorderReferenceNumber: response.customsOfficeAtBorderReferenceNumber ?? '',
    identificationNumber: response.identificationNumber ?? '',
    nationality: response.nationality ?? '',
    sequenceNumber: response.sequenceNumber ?? index,
    typeOfIdentification: response.typeOfIdentification ?? '',
    typeOfMeansOfTransport: response.typeOfMeansOfTransport ?? '',
    estimatedDateAndTimeOfArrival: formatToLocalDateTimeString(response.estimatedDateAndTimeOfArrival),
    estimatedDateAndTimeOfDeparture: formatToLocalDateTimeString(response.estimatedDateAndTimeOfDeparture),
    actualDateAndTimeOfDeparture: formatToLocalDateTimeString(response.actualDateAndTimeOfDeparture),
    countriesOfRouting: response?.countryOfRoutings?.map(parseCountryOfRoutingResponse) ?? [],
  }
}

export function parseDepartureTransportMeansResponse(
  response: DepartureTransportMeansResponse,
  index: number,
): DepartureTransportMeansType {
  return {
    id: response.id,
    deleted: false,
    identificationNumber: response.identificationNumber ?? '',
    nationality: response.nationality ?? '',
    sequenceNumber: response.sequenceNumber ?? index,
    typeOfIdentification: response.typeOfIdentification ?? '',
  }
}

export function parseLocationOfGoods(response: LocationOfGoodsResponse | undefined): LocationOfGoodsType {
  return {
    id: response?.id ?? null,
    deleted: false,
    additionalIdentifier: response?.additionalIdentifier ?? '',
    address: parseAddressResponse(response?.address),
    authorisationNumber: response?.authorisationNumber ?? '',
    contactPerson: {
      deleted: false,
      email: response?.contactPerson?.email ?? '',
      id: response?.contactPerson?.id ?? null,
      name: response?.contactPerson?.name ?? '',
      telephone: response?.contactPerson?.telephone ?? '',
    },
    postcodeAddress: response !== undefined ? {
      houseNumber: response?.houseNumber ?? '',
      postcode: response?.postcode ?? '',
      country: response?.country ?? '',
    } : null,
    economicOperatorIdentificationNumber: response?.economicOperatorIdentificationNumber ?? '',
    gnss: null,
    qualifierOfIdentification: response?.qualifierOfIdentification ?? '',
    referenceNumber: response?.referenceNumber ?? '',
    typeOfLocation: response?.typeOfLocation ?? '',
    unLocode: response?.unLocode ?? '',
  }
}

export function fromConsignmentResponse(response?: ConsignmentResponse | null): Consignment {
  return {
    id: response?.id ?? null,
    carrier: parseCarrierConsignmentDetailResponse(response?.carrier),
    consignee: parseConsigneeConsignmentDetailResponse(response?.consignee),
    consignor: parseConsignorConsignmentDetailResponse(response?.consignor),
    countryOfDispatch: response?.countryOfDispatch ?? '',
    countryOfDestination: response?.countryOfDestination ?? '',
    grossMass: (!Number.isNaN(response?.grossMass) ? response?.grossMass : null) ?? 0,
    transportChargesMethodOfPayment: response?.transportChargesMethodOfPayment ?? '',
    containerIndicator: response?.containerIndicator === true ? 'true' : 'false',
    referenceNumberUCR: response?.referenceNumberUcr ?? '',
    borderModeOfTransport: response?.borderModeOfTransport ?? '',
    inlandModeOfTransport: response?.inlandModeOfTransport ?? '',
    placeOfLoading: parsePlaceOfLoading(response?.loading),
    placeOfUnloading: parsePlaceOfUnloading(response?.unloading),
    placeOfDelivery: parsePlaceOfDelivery(response?.placeOfDelivery),
    placeOfAcceptance: parsePlaceOfAcceptance(response?.placeOfAcceptance),
    activeBorderTransportMeans: response?.activeBorderTransportMeans.map(parseActiveBorderTransportMeansResponse) ?? [],
    departureTransportMeans: response?.departureTransportMeans.map(parseDepartureTransportMeansResponse) ?? [],
    countryOfRoutingOfConsignment: response?.countryOfRoutings.map(parseCountryOfRoutingResponse) ?? [],
    transportEquipment: response?.transportEquipments.map(parseTransportEquipmentResponse) ?? [],
    additionalSupplyChainActor: response?.additionalSupplyChainActors.map(parseAdditionalSupplyChainActorResponse) ?? [],
    locationOfGoods: parseLocationOfGoods(response?.locationOfGoods),
  }
}

export function mapToLocationOfGoodsAddressCreateOrUpdateRequest(
  locationOfGoods: LocationOfGoodsType,
  transitOperationId: number,
): AddressRequest | null {
  const address = locationOfGoods?.address
  && nonNullAddress(locationOfGoods?.address) ? locationOfGoods?.address : null

  if (!address) return null

  return toCreateOrUpdateAddressRequest(locationOfGoods?.address, transitOperationId)
}

export function toConsignmentRequest(consignmentUpdate: ConsignmentSave): CreateOrUpdateConsignmentRequest {
  if (consignmentUpdate.transitOperationId === null) throw Error('Missing transitOperationId')

  const { transitOperationId } = consignmentUpdate

  consignmentUpdate.countryOfRoutingOfConsignment.sort(sortBySequenceNumber)
  consignmentUpdate.transportEquipment.sort(sortBySequenceNumber)
  consignmentUpdate.additionalSupplyChainActor.sort(sortBySequenceNumber)
  consignmentUpdate.activeBorderTransportMeans.sort(sortBySequenceNumber)
  consignmentUpdate.departureTransportMeans.sort(sortBySequenceNumber)

  return {
    transitOperationId,
    countryOfDispatch: consignmentUpdate.countryOfDispatch,
    countryOfDestination: consignmentUpdate.countryOfDestination,
    grossMass: consignmentUpdate.grossMass,
    transportChargesMethodOfPayment: consignmentUpdate.transportChargesMethodOfPayment ?? '',
    containerIndicator: consignmentUpdate.containerIndicator === 'true',
    referenceNumberUcr: consignmentUpdate.referenceNumberUCR ?? undefined,
    borderModeOfTransport: consignmentUpdate.borderModeOfTransport,
    inlandModeOfTransport: consignmentUpdate.inlandModeOfTransport,
    loadingPlace: toCreateOrUpdatePlaceRequest(consignmentUpdate.placeOfLoading),
    unloadingPlace: toCreateOrUpdatePlaceRequest(consignmentUpdate.placeOfUnloading),
    placeOfDelivery: toCreateOrUpdatePlaceRequest(consignmentUpdate.placeOfDelivery),
    placeOfAcceptance: toCreateOrUpdatePlaceRequest(consignmentUpdate.placeOfAcceptance),
    goodsLocation: consignmentUpdate.locationOfGoods ? {
      id: consignmentUpdate.locationOfGoods.id ?? null,
      transitOperationId,
      typeOfLocation: consignmentUpdate.locationOfGoods.typeOfLocation ?? '',
      qualifierOfIdentification: consignmentUpdate.locationOfGoods.qualifierOfIdentification ?? '',
      authorisationNumber: consignmentUpdate.locationOfGoods.authorisationNumber ?? '',
      additionalIdentifier: consignmentUpdate.locationOfGoods.additionalIdentifier ?? '',
      unLocode: consignmentUpdate.locationOfGoods.unLocode ?? '',
      referenceNumber: consignmentUpdate.locationOfGoods.referenceNumber ?? '',
      economicOperatorIdentificationNumber: consignmentUpdate.locationOfGoods.economicOperatorIdentificationNumber ?? '',
      address: mapToLocationOfGoodsAddressCreateOrUpdateRequest(consignmentUpdate.locationOfGoods, transitOperationId),
      contactPerson: {
        id: consignmentUpdate.locationOfGoods.contactPerson?.id ?? null,
        name: consignmentUpdate.locationOfGoods.contactPerson?.name ?? '',
        email: consignmentUpdate.locationOfGoods.contactPerson?.email ?? '',
        telephone: consignmentUpdate.locationOfGoods.contactPerson?.telephone ?? '',
        transitOperationId,
      },
      houseNumber: consignmentUpdate.locationOfGoods.postcodeAddress?.houseNumber ?? '',
      postcode: consignmentUpdate.locationOfGoods.postcodeAddress?.postcode ?? '',
      country: consignmentUpdate.locationOfGoods.postcodeAddress?.country ?? '',
    } : null,
    consigneeId: consignmentUpdate.consigneeId ?? undefined,
    consignorId: consignmentUpdate.consignorId ?? undefined,
    carrierId: consignmentUpdate.carrierId ?? undefined,
    countryOfRoutings: consignmentUpdate.countryOfRoutingOfConsignment
      .filter(excludeDeleted)
      .map(toCreateOrUpdateCountryOfRoutingRequest),
    transportEquipments: consignmentUpdate.transportEquipment
      .filter(excludeDeleted)
      .map((equipment, index) => toCreateOrUpdateTransportEquipmentRequest(equipment, index)),
    additionalSupplyChainActors: consignmentUpdate.additionalSupplyChainActor
      .filter(excludeDeleted)
      .map(toCreateOrUpdateAdditionalSupplyChainActorRequest),
    activeBorderTransportMeans: consignmentUpdate.activeBorderTransportMeans
      .filter(excludeDeleted)
      .map(toCreateOrUpdateActiveBorderTransportMeansRequest),
    departureTransportMeans: consignmentUpdate.departureTransportMeans
      .filter(excludeDeleted)
      .map(toCreateOrUpdateDepartureTransportMeansRequest),
  }
}

export function fromConsignmentSummaryResponse(response: ConsignmentSummaryResponse): ConsignmentSummary {
  return {
    numberOfItems: response.numberOfItems ?? 0,
    numberOfPackages: response.numberOfPackages ?? 0,
    totalGrossWeight: response.totalGrossWeight ?? 0,
  }
}

export function fromGrossMassValidationResponse(response: GrossMassValidationResponse): GrossMassValidationResult {
  const houseConsignmentRequirements = response.houseConsignmentRequirements ?? []
  return {
    valid: response.valid ?? true,
    consignmentRequirement: response.consignmentRequirement ? {
      actual: response.consignmentRequirement.actual ?? 0,
      expectedMinimum: response.consignmentRequirement.expectedMinimum ?? 0,
    } : null,
    houseConsignmentRequirements: houseConsignmentRequirements.map((requirement) => ({
      houseConsignmentId: requirement.houseConsignmentId ?? null,
      actual: requirement.actual ?? 0,
      expectedMinimum: requirement.expectedMinimum ?? 0,
    })),
  }
}
